import Joi from 'joi'
import {
  CarouselCard,
  ComponentType,
  ContentColumnDefinition,
  ContentColumnsComponentDefinition,
  ContentColumnType,
  FeatureColumn,
  FeatureColumnsComponentDefinition,
  FooterComponentDefinition,
  FullWidthImageComponentDefinition,
  NonFullWidthImageComponentDefinition,
  FullWidthImageOverlayComponentDefinition,
  HeaderComponentDefinition,
  HeroComponentDefinition,
  ImageDefinition,
  InvitationComponentDefinition,
  EmailBoxComponentDefinition,
  PageDefinition,
  ParagraphComponentDefinition,
  PrimaryColorVarieties,
  ThemeDefinition,
  VideoComponentDefinition,
  HeroCopyrightPosition
} from '../../data/types'
import { GalleryComponentDefinition } from '../../data/types/components/GalleryComponentDefinition'
import { HeroCopyrightDefinition } from '../../data/types/components/HeroCopyrightDefinition'
import MediaQueryName from '../../data/types/MediaQueryName'
import { StyleBlock } from '../../data/types/StyleBlock'
import { HyperlinkDefinition } from '../../data/types/HyperlinkDefinition'
import { MetadataDefinition } from '../../data/types/MetadataDefinition'
import { ParagraphDefinition } from '../../data/types/ParagraphDefinition'
import { SocialMediaLinksDefinition } from '../../data/types/SocialMediaLinksDefinition'
import { VideoDefinition } from '../../data/types/VideoDefinition'
import { VideoSourceDefinition } from '../../data/types/VideoSourceDefinition'

const imageSchema = Joi.object<ImageDefinition>({
  type: Joi.allow('IMAGE'),
  src: Joi.string()
}).unknown()

const hyperlinkSchema = Joi.object<HyperlinkDefinition>({
  href: Joi.string(),
  text: Joi.string(),
  isPrimary: Joi.boolean().allow(null)
}).unknown()

const styleBlockSchema = Joi.array().items(
  Joi.object<StyleBlock>({
    mediaQuery: Joi.allow(...Object.values(MediaQueryName)),
    paddingHeight: Joi.number().integer().optional().allow(null)
  }).unknown()
)

const paragraphSchemaObject = {
  type: Joi.allow('PARAGRAPH'),
  textAlignment: Joi.any()
    .allow(...Object.values(ComponentType))
    .optional(),
  sections: Joi.any(),
  button: hyperlinkSchema.allow(null).optional(),
  styles: styleBlockSchema
}

const paragraphSchema = Joi.object<ParagraphDefinition>(paragraphSchemaObject).unknown()

const pageSchema = Joi.object<PageDefinition>({
  components: Joi.array().items(
    Joi.alternatives().try(
      Joi.object<HeaderComponentDefinition>({
        type: Joi.any().allow(ComponentType.Hero),
        button: hyperlinkSchema.optional(),
        comment: Joi.string().allow(null)
      }).unknown(),

      Joi.object<HeroComponentDefinition>({
        type: Joi.any().allow(ComponentType.Header),
        blurb: Joi.string(),
        genre: Joi.string(),
        images: Joi.array().items(
          Joi.object<CarouselCard>({
            image: imageSchema,
            mobileImage: imageSchema
          })
        ),
        copyright: Joi.object<HeroCopyrightDefinition>({
          color: Joi.string().allow(null).optional(),
          text: Joi.string().allow(null).optional(),
          position: Joi.allow(...Object.values(HeroCopyrightPosition))
        })
          .optional()
          .allow(null)
          .unknown(),
        season: Joi.string(),
        title: Joi.string(),
        year: Joi.string(),
        rating: Joi.string(),
        button: hyperlinkSchema.optional(),
        comment: Joi.string().allow(null)
      }).unknown(),

      Joi.object<FullWidthImageComponentDefinition>({
        type: Joi.any().allow(ComponentType.FullWidthImage),
        image: imageSchema,
        comment: Joi.string().allow(null)
      }).unknown(),

      Joi.object<NonFullWidthImageComponentDefinition>({
        type: Joi.any().allow(ComponentType.NonFullWidthImage),
        image: imageSchema,
        comment: Joi.string().allow(null)
      }).unknown(),

      Joi.object<FullWidthImageOverlayComponentDefinition>({
        type: Joi.any().allow(ComponentType.FullWidthImageOverlay),
        image: imageSchema,
        mobileImage: imageSchema,
        text: Joi.string(),
        watermark: imageSchema.optional(),
        comment: Joi.string().allow(null)
      }).unknown(),

      Joi.object<ParagraphComponentDefinition>({
        ...paragraphSchemaObject,
        type: Joi.any().allow(ComponentType.Paragraph),
        comment: Joi.string().allow(null)
      }).unknown(),

      Joi.object<FeatureColumnsComponentDefinition>({
        type: Joi.any().allow(ComponentType.FeatureColumns),
        columns: Joi.array().items(
          Joi.object<FeatureColumn>({
            image: imageSchema,
            text: paragraphSchema
          }).unknown()
        ),
        paragraphStyles: styleBlockSchema,
        title: Joi.string().allow(null),
        comment: Joi.string().allow(null)
      }).unknown(),

      Joi.object<ContentColumnsComponentDefinition>({
        backgroundImage: imageSchema.optional(),
        type: Joi.any().allow(ComponentType.ContentColumns),
        columns: Joi.array().items(
          Joi.alternatives().try(
            Joi.object<ContentColumnDefinition>({
              ...paragraphSchemaObject,
              type: ContentColumnType.Text
            }).unknown(),
            Joi.object<ContentColumnDefinition>({
              type: ContentColumnType.Image,
              src: Joi.string()
            }).unknown()
          )
        ),
        comment: Joi.string().allow(null)
      }).unknown(),

      Joi.object<FooterComponentDefinition>({
        type: Joi.any().allow(ComponentType.Footer),
        mainLinks: Joi.array().items(hyperlinkSchema),
        secondaryLinks: Joi.array().items(hyperlinkSchema),
        socialMedia: Joi.object<SocialMediaLinksDefinition>({
          facebook: Joi.string(),
          instagram: Joi.string(),
          tiktok: Joi.string()
        }).unknown(),
        comment: Joi.string().allow(null)
      }).unknown(),

      Joi.object<FooterComponentDefinition>({
        type: Joi.any().allow(ComponentType.Footer),
        mainLinks: Joi.array().items(hyperlinkSchema),
        copyText: Joi.string(),
        secondaryLinks: Joi.array().items(hyperlinkSchema),
        socialMedia: Joi.object<SocialMediaLinksDefinition>({
          facebook: Joi.string(),
          instagram: Joi.string(),
          tiktok: Joi.string()
        }).unknown(),
        comment: Joi.string().allow(null)
      }).unknown(),

      Joi.object<InvitationComponentDefinition>({
        type: Joi.any().allow(ComponentType.Invitation),
        header: Joi.string(),
        image: imageSchema,
        mobileImage: imageSchema.optional(),
        text: Joi.string(),
        button: hyperlinkSchema,
        comment: Joi.string().allow(null)
      }).unknown(),

      Joi.object<VideoComponentDefinition>({
        type: Joi.any().allow(ComponentType.Video),
        autoplay: Joi.boolean().optional(),
        video: Joi.object<VideoDefinition>({
          poster: Joi.string(),
          sources: Joi.array().items(
            Joi.object<VideoSourceDefinition>({
              ext_x_version: Joi.string().optional().allow(null),
              key_systems: Joi.string().optional().allow(null),
              profiles: Joi.string().optional().allow(null),
              src: Joi.string(),
              type: Joi.string(),
              __typename: Joi.allow('Sources').optional()
            })
          )
        }),
        comment: Joi.string().allow(null)
      }).unknown(),

      Joi.object<GalleryComponentDefinition>({
        type: Joi.any().allow(ComponentType.Gallery),
        title: Joi.string().optional(),
        images: Joi.array().items(imageSchema),
        comment: Joi.string().allow(null)
      }).unknown(),

      Joi.object<EmailBoxComponentDefinition>({
        type: Joi.any().allow(ComponentType.EmailBox),
        title: Joi.string(),
        description: Joi.any(),
        billingPeriod: Joi.string().allow(null),
        originalPrice: Joi.number().allow(null),
        offerPrice: Joi.number().allow(null),
        voucher: Joi.string().allow(null),
        comment: Joi.string().allow(null)
      }).unknown()
    )
  ),

  theme: Joi.object<ThemeDefinition>({
    primaryColor: Joi.object<PrimaryColorVarieties>({
      default: Joi.string(),
      light: Joi.string(),
      dark: Joi.string()
    })
  }).unknown(),

  pageTitle: Joi.string().optional().allow(null),

  metadata: Joi.array()
    .items(
      Joi.object<MetadataDefinition>({
        content: Joi.string(),
        name: Joi.string().allow(null).optional(),
        property: Joi.string()
      }).unknown()
    )
    .optional()
    .allow(null)
}).unknown()

export default pageSchema
